import React, {useCallback, useEffect, useRef, useState} from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {useMediaQuery, useTheme} from '@mui/material';
import './ArrDrawer.scss';
import ArrButton from '../arr-button/ArrButton';
import {t} from 'i18next';
import {panelWidths, transition_time_default} from '../../../shared/constants';
import {RightPanelProps} from '../../../shared/types';

const MAX_DRAWER_WIDTH_RATIO = 0.5;

const getDefaultWidthPx = (size: 'small' | 'large') => {
  const rootFontSize =
    parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const widthRem = size === 'large' ? panelWidths.large : panelWidths.small;
  return widthRem * rootFontSize;
};

const canResizeDrawer = (size: 'small' | 'large') => {
  const defaultWidthPx = getDefaultWidthPx(size);
  const maxWidthPx = window.innerWidth * MAX_DRAWER_WIDTH_RATIO;
  return defaultWidthPx < maxWidthPx;
};

type CommonPanelProps = {
  isOpen: boolean;
  title?: string;
  children?: React.ReactNode;
  onClose?: () => void;
  trapFocus?: boolean;
  className?: string;
  ariaLabel?: string;
  showButton: boolean;
  transition?: number;
  showHeader?: boolean;
  scrollable?: boolean;
  closeButtonAriaLabel?: string;
};

export type PanelProps = CommonPanelProps & RightPanelProps;

export const ArrDrawer: React.FC<PanelProps> = ({
  isOpen,
  title,
  children,
  onClose,
  trapFocus = true,
  className = '',
  ariaLabel,
  size,
  side = 'right',
  showButton = true,
  transition = transition_time_default,
  showHeader = true,
  scrollable = true,
  closeButtonAriaLabel = t('common.close'),
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const width = size === 'large' ? panelWidths.large : panelWidths.small;
  const isRightSide = side === 'right';
  const enableResize = !isSmallScreen && canResizeDrawer(size);

  const [drawerWidth, setDrawerWidth] = useState(() => getDefaultWidthPx(size));
  const [isResizing, setIsResizing] = useState(false);

  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      setDrawerWidth(getDefaultWidthPx(size));
    }
  }, [isOpen, size]);

  const getMinWidth = useCallback(() => getDefaultWidthPx(size), [size]);

  const getMaxWidth = useCallback(
    () => window.innerWidth * MAX_DRAWER_WIDTH_RATIO,
    []
  );

  const handleResizeMove = useCallback(
    (clientX: number) => {
      const delta = isRightSide
        ? startXRef.current - clientX
        : clientX - startXRef.current;
      const newWidth = Math.min(
        getMaxWidth(),
        Math.max(getMinWidth(), startWidthRef.current + delta)
      );
      setDrawerWidth(newWidth);
    },
    [getMaxWidth, getMinWidth, isRightSide]
  );

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = drawerWidth;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [drawerWidth]
  );

  const handleResizePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isResizing) return;
      handleResizeMove(e.clientX);
    },
    [handleResizeMove, isResizing]
  );

  const stopResize = useCallback(
    (e: React.PointerEvent) => {
      if (!isResizing) return;
      setIsResizing(false);
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    },
    [isResizing]
  );

  const panelWidth = isSmallScreen
    ? '100%'
    : enableResize
      ? `${drawerWidth}px`
      : `${width}rem`;

  const paperPositionSx = isRightSide
    ? {right: 0, left: 'auto'}
    : {left: 0, right: 'auto'};

  const drawerContent = (
    <>
      {showHeader && (
        <Box className="arr-drawer__header">
          {title && (
            <Typography
              id="arr-drawer-title"
              variant="h6"
              tabIndex={0}
              component="h2"
              className="arr-drawer__title"
              data-testid={`arr-drawer-title`}
            >
              {title}
            </Typography>
          )}
          {showButton && (
            <ArrButton
              id={t('common.close')}
              name={t('common.close')}
              variant="secondary"
              size="medium"
              onClick={onClose}
              aria-label={closeButtonAriaLabel}
              className="arr-drawer__close-button"
              dataTestId="close-btn"
            >
              {t('common.close')}
            </ArrButton>
          )}
        </Box>
      )}
      <Box
        component="main"
        className={`arr-drawer__content ${scrollable ? 'scrollable' : ''}`}
      >
        {children}
      </Box>
    </>
  );

  return (
    <Drawer
      anchor={side}
      transitionDuration={transition}
      open={isOpen}
      className="arr-panel"
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
        BackdropProps: {
          className: 'arr-drawer__backdrop',
        },
      }}
      slotProps={{
        paper: {
          className: `arr-drawer__panel ${className ?? ''} ${scrollable ? 'arr-drawer--scrollable' : ''} ${enableResize ? 'arr-drawer__panel--resizable' : ''}`,
          sx: {
            width: panelWidth,
            ...(enableResize ? paperPositionSx : {}),
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
          },
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': title ? 'arr-drawer-title' : undefined,
          'aria-label': !title ? ariaLabel : undefined,
        },
      }}
      disableEnforceFocus={!trapFocus}
    >
      {enableResize ? (
        <Box className="arr-drawer__inner">
          <Box
            className={`arr-drawer__resize-handle arr-drawer__resize-handle--${side}`}
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={stopResize}
            onPointerCancel={stopResize}
            data-testid="arr-drawer-resize-handle"
            role="separator"
            aria-orientation="vertical"
          />
          {drawerContent}
        </Box>
      ) : (
        drawerContent
      )}
    </Drawer>
  );
};
